import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Typography,
  Stack,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  FormHelperText,
  Box,
  FormControlLabel,
  Checkbox,
  ListItemText,
  useMediaQuery,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import CloseIcon from "@mui/icons-material/Close";
import { AddCircle } from "@mui/icons-material";
import ImageUpload from "./components/ImageUpload";
import PdfUpload from "./components/PdfUpload";
import { CRAppPromotions_Update } from "./PromotionAction";
import moment from "moment";
import { fAlertToast } from "../../Utility/Utilitys";
import TransLocationSelect from "./components/TransLocationSelect";

const PromotionModal = ({
  open,
  setOpen,
  promoData,
  setPromoData,
  handleNew,
  locations,
  vRdxuserInfo,
  onClose,
}) => {
  const inputRefs = useRef({});
  const [errors, setErrors] = useState({});
  const isMobile = useMediaQuery("(max-width:650px)");
  // Close modal
  const handleClose = () => {
    setOpen(false);
    setErrors({});
    if (onClose) {
      onClose(); // Trigger the parent's onClose after modal is closed
    }
  };

  // Handle input changes
  const handleInputChange = (key, value) => {
    setPromoData((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: false }));
  };

  // Add Image Input
  const addImageInput = () => {
    if (promoData.images.length < 30) {
      setPromoData((prev) => ({
        ...prev,
        images: [...prev.images, ""],
      }));
    }
  };

  // Remove Image Input
  const removeImageInput = (index) => {
    if (promoData.images.length > 4) {
      setPromoData((prev) => ({
        ...prev,
        images: prev.images.filter((_, i) => i !== index),
      }));
    }
  };

  // Add PDF Input
  const addPdfInput = () => {
    if (promoData.pdfs.length < 5) {
      setPromoData((prev) => ({
        ...prev,
        pdfs: [...prev.pdfs, ""],
      }));
    }
  };

  // Remove PDF Input
  const removePdfInput = (index) => {
    if (promoData.pdfs.length > 1) {
      setPromoData((prev) => ({
        ...prev,
        pdfs: prev.pdfs.filter((_, i) => i !== index),
      }));
    }
  };

  function fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result); // Base64 string
      reader.onerror = (error) => reject(error);
    });
  }

  const handleFileChange = async (event, index, type) => {
    const file = event.target.files[0];
    if (!file) return; // Early return if no file is selected.
    const IMG_MAX_SIZE = 1 * 1024 * 1024; // 1MB in bytes
    const PDF_MAX_SIZE = 25 * 1024 * 1024; // 25MB in bytes
    // File type validation
    // if (type === "images") {
    //   if (!file.name.toLowerCase().includes("jpg")) {
    //     fAlertToast("FAILED", "Only JPG images are allowed.");
    //     return;
    //   }
    //   if (file.size > IMG_MAX_SIZE) {
    //     fAlertToast("FAILED", "Image must be less than 1MB.");
    //     return;
    //   }
    // }
    if (type === "pdfs") {
      if (file.type !== "application/pdf") {
        fAlertToast("FAILED", "Only PDF files are allowed.");
        return;
      }
      if (file.size > PDF_MAX_SIZE) {
        fAlertToast("FAILED", "PDF must be less than 25MB.");
        return;
      }
    }
    const base64 = await fileToBase64(file);
    setPromoData((prev) => ({
      ...prev,
      [type]: prev[type]?.map((item, i) => (i === index ? base64 : item)),
    }));
  };

  // Validate and Save
  const handleUpdate = async (status) => {
    let newErrors = {};

    // Validate text fields
    if (!promoData.promoStartDate) newErrors.promoStartDate = true;
    if (!promoData.promoEndDate) newErrors.promoEndDate = true;
    if (!promoData.transLocation || promoData.transLocation.length === 0) {
      newErrors.transLocation = true;
    }
    if (!promoData.location) newErrors.location = true;
    if (!promoData.promoNameEn) newErrors.promoNameEn = true;
    if (!promoData.narration) newErrors.narration = true;
    if (!promoData.NotificationContent) newErrors.NotificationContent = true;

    // Filter valid images (jpg, jpeg only)
    const validImages = promoData?.images?.filter((base64) => base64);

    // Filter valid PDFs
    const validPdfs = promoData?.pdfs?.filter((base64) => base64);

    // Check for minimum 5 images
    if (validImages.length < 4) {
      fAlertToast("WARNING", "Please upload at least 4 images.");
      return;
    }

    // Check for minimum 1 PDF
    if (validPdfs.length < 1) {
      fAlertToast("WARNING", "Please upload at least 1 PDF.");
      return;
    }

    setErrors(newErrors);
    try {
      // Proceed only if no text field errors
      if (Object.keys(newErrors).length === 0) {
        const payload = {
          images: validImages,
          pdf: validPdfs,
          UId: promoData.promoId,
          NameEN: promoData.promoNameEn,
          NameFL: promoData.promoNameFl || "",
          Loc: promoData.location,
          LocationCSV: promoData.transLocation.toString(),
          Narr: promoData.narration,
          UserCd: vRdxuserInfo?.UserID || "",
          Active: promoData.isActive ? "Y" : "N",
          Status: status === "SAVE" ? "ENTER" : "POST",
          NotificationContent: promoData.NotificationContent,
          FromDate: moment(promoData.promoStartDate).format("MM/DD/YYYY"),
          ToDate: moment(promoData.promoEndDate).format("MM/DD/YYYY"),
        };
        const res = await CRAppPromotions_Update(payload);
        if (res?.status === 200 && res?.data?.recordset) {
          handleNew(); // Reset form on success
          handleClose();
          if (status === "SAVE") fAlertToast("SUCCESS", "Saved Successfully");
          else fAlertToast("SUCCESS", "Posted Successfully");
        } else {
          fAlertToast(
            "FAILED",
            res?.response?.data?.details + " " + res?.response?.data?.error
          );
        }
      }
    } catch (error) {
      console.log(error);
      fAlertToast("FAILED", error?.message);
    }
  };

  useEffect(() => {
    if (open) {
      // Delay to ensure the modal is fully open
      setTimeout(() => {
        inputRefs.current["promoStartDate"]?.focus();
      }, 200);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
    >
      {/* Header Section */}
      <DialogTitle
        sx={{
          bgcolor: "primary.main",
          color: "white",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: " 4px 8px !important",
        }}
      >
        <Typography sx={{ fontWeight: "bold" }}>Promo Details</Typography>
        <IconButton onClick={handleClose} sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Scrollable Content */}
      <DialogContent
        dividers
        sx={{
          maxHeight: "90vh",
          padding: { xs: "8px", sm: "12px", md: "16px 24px" },
        }}
      >
        <Grid container spacing={1.4} marginBottom={2}>
          <Grid item xs={3} sm={1.9} md={1.5}>
            <TextField
              label="Promo ID"
              value={promoData.promoId}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              disabled
            />
          </Grid>
          <Grid item xs={4.5} sm={2.3} md={2}>
            <DatePicker
              label="Start Date"
              inputFormat="DD/MM/YYYY"
              value={promoData.promoStartDate}
              onChange={(value) => handleInputChange("promoStartDate", value)}
              inputRef={(el) => (inputRefs.current["promoStartDate"] = el)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  autoComplete="off"
                  error={errors?.promoStartDate}
                  helperText={
                    errors?.promoStartDate && "Start Date is required"
                  }
                  FormHelperTextProps={{
                    sx: { fontSize: "10px", marginLeft: 0 }, // Reduced font size and aligned text
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={4.5} sm={2.3} md={2}>
            <DatePicker
              label="End Date"
              value={promoData.promoEndDate}
              inputFormat="DD/MM/YYYY"
              inputRef={(el) => (inputRefs.current["promoEndDate"] = el)}
              onChange={(value) => handleInputChange("promoEndDate", value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  size="small"
                  autoComplete="off"
                  InputLabelProps={{ shrink: true }}
                  error={errors?.promoEndDate}
                  helperText={errors?.promoEndDate && "End Date is required"}
                  FormHelperTextProps={{
                    sx: { fontSize: "10px", marginLeft: 0 }, // Reduced font size and aligned text
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={6} sm={2.5} md={1.5}>
            <FormControl fullWidth size="small" error={errors?.location}>
              <InputLabel shrink id="location-label">
                Trans Location
              </InputLabel>
              <Select
                size="small"
                label="Trans Location"
                labelId="location-label"
                sx={{
                  height: "33px !important",
                }}
                value={promoData.location}
                inputRef={(el) => (inputRefs.current["location"] = el)}
                onChange={(e) => handleInputChange("location", e.target.value)}
              >
                {locations &&
                  locations?.map((loc) => (
                    <MenuItem className="menuSm" key={loc.Uid} value={loc.Uid}>
                      {loc.Name1}
                    </MenuItem>
                  ))}
              </Select>
              {errors?.location && (
                <FormHelperText sx={{ fontSize: "10px", marginLeft: 0 }}>
                  Location is required
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <TransLocationSelect
              locations={locations}
              promoData={promoData}
              setPromoData={setPromoData}
              errors={errors}
              setErrors={setErrors}
              inputRefs={inputRefs}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={3.5}>
            <TextField
              label="Promo Name English"
              value={promoData.promoNameEn}
              inputRef={(el) => (inputRefs.current["promoNameEn"] = el)}
              onChange={(e) => handleInputChange("promoNameEn", e.target.value)}
              fullWidth
              size="small"
              autoComplete="off"
              error={errors?.promoNameEn}
              InputLabelProps={{ shrink: true }}
              helperText={
                errors?.promoNameEn && "Promotion Name EN is required"
              }
              FormHelperTextProps={{
                sx: { fontSize: "10px", marginLeft: 0 }, // Reduced font size and aligned text
              }}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <TextField
              label="Promo Name FL"
              value={promoData.promoNameFl}
              inputRef={(el) => (inputRefs.current["promoNameFl"] = el)}
              onChange={(e) => handleInputChange("promoNameFl", e.target.value)}
              fullWidth
              size="small"
              autoComplete="off"
              error={errors?.promoNameFl}
              InputLabelProps={{ shrink: true }}
              // helperText={errors?.promoNameFl && "Promotion name FL is required"}
              FormHelperTextProps={{
                sx: { fontSize: "10px", marginLeft: 0 }, // Reduced font size and aligned text
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Narration"
              value={promoData.narration}
              inputRef={(el) => (inputRefs.current["narration"] = el)}
              onChange={(e) => handleInputChange("narration", e.target.value)}
              fullWidth
              size="small"
              autoComplete="off"
              error={errors?.narration}
              InputLabelProps={{ shrink: true }}
              helperText={errors?.narration && "Narration name is required"}
              FormHelperTextProps={{
                sx: { fontSize: "10px", marginLeft: 0 }, // Reduced font size and aligned text
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Notification Content"
              value={promoData.NotificationContent}
              inputRef={(el) => (inputRefs.current["NotificationContent"] = el)}
              onChange={(e) =>
                handleInputChange("NotificationContent", e.target.value)
              }
              fullWidth
              size="small"
              autoComplete="off"
              error={errors?.NotificationContent}
              InputLabelProps={{ shrink: true }}
              helperText={
                errors?.NotificationContent &&
                "Notification Content is required"
              }
              FormHelperTextProps={{
                sx: { fontSize: "10px", marginLeft: 0 }, // Reduced font size and aligned text
              }}
            />
          </Grid>
          <Grid item xs={4} sm={4} md={1}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={promoData.isActive}
                  onChange={(e) =>
                    handleInputChange("isActive", e.target.checked)
                  }
                />
              }
              label="Active"
            />
          </Grid>
        </Grid>
        {/* Dynamic Image Inputs (Min 5) */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5, // Spacing between items
            justifyContent: "flex-start",
          }}
        >
          {promoData?.images?.map((image, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <ImageUpload
                image={image}
                index={index}
                handleFileChange={handleFileChange}
                removeImageInput={removeImageInput}
              />
            </Box>
          ))}
        </Box>
        <Box>
          <Button
            size="small"
            startIcon={<AddCircle />}
            onClick={addImageInput}
            disabled={promoData.images.length >= 30}
          >
            Add Image
          </Button>
        </Box>
        {/* Dynamic PDF Inputs (Min 1) */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5, // Spacing between items
            justifyContent: "flex-start",
          }}
        >
          {promoData?.pdfs?.map((pdf, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <PdfUpload
                pdf={pdf}
                index={index}
                handleFileChange={handleFileChange}
                removePdfInput={removePdfInput}
              />
            </Box>
          ))}
        </Box>
        <Box>
          <Button
            size="small"
            startIcon={<AddCircle />}
            onClick={addPdfInput}
            disabled={promoData.pdfs.length >= 5}
          >
            Add PDF
          </Button>
        </Box>
      </DialogContent>

      {/* Action Buttons */}
      <DialogActions>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            size="small"
            color="success"
            onClick={() => handleUpdate("SAVE")}
            disabled={promoData?.promoStatus === "POST"}
          >
            Save
          </Button>
          <Button
            variant="contained"
            size="small"
            color="primary"
            onClick={() => handleUpdate("POST")}
            disabled={promoData?.promoStatus === "POST"}
          >
            Post
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default PromotionModal;
